/** Lightweight JSON Schema helpers for discovery preview forms. */

export type JsonSchema = {
  type?: string | string[]
  title?: string
  description?: string
  properties?: Record<string, JsonSchema>
  required?: string[]
  enum?: unknown[]
  items?: JsonSchema
  format?: string
  const?: unknown
  default?: unknown
  if?: JsonSchema
  then?: JsonSchema
  else?: JsonSchema
  allOf?: JsonSchema[]
  oneOf?: JsonSchema[]
  dependencies?: Record<string, JsonSchema | string[]>
  minimum?: number
  maximum?: number
  minLength?: number
  maxLength?: number
  uniqueItems?: boolean
  minItems?: number
}

export type UiSchema = Record<string, { 'ui:widget'?: string } | undefined>

export type EffectiveSchema = {
  properties: Record<string, JsonSchema>
  required: Set<string>
}

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}

/** True when ``values`` satisfy a simple JSON Schema condition (const / enum / required). */
export function matchesCondition(
  condition: JsonSchema | undefined,
  values: Record<string, unknown>,
): boolean {
  if (!condition) return true
  const req = condition.required ?? []
  for (const key of req) {
    const v = values[key]
    if (v === undefined || v === null || v === '') return false
  }
  const props = condition.properties ?? {}
  for (const [key, sub] of Object.entries(props)) {
    const v = values[key]
    if (sub.const !== undefined) {
      if (v !== sub.const) return false
      continue
    }
    if (sub.enum) {
      if (v === undefined || v === null || v === '') return false
      if (!sub.enum.includes(v)) return false
    }
  }
  return true
}

function mergeBranch(
  into: EffectiveSchema,
  branch: JsonSchema | undefined,
): void {
  if (!branch) return
  for (const [k, v] of Object.entries(branch.properties ?? {})) {
    into.properties[k] = v
  }
  for (const r of branch.required ?? []) into.required.add(r)
}

function applyDependencies(
  into: EffectiveSchema,
  deps: Record<string, JsonSchema | string[]> | undefined,
  values: Record<string, unknown>,
): void {
  if (!deps) return
  for (const [key, dep] of Object.entries(deps)) {
    const present = values[key] !== undefined && values[key] !== null && values[key] !== ''
    if (!present) continue
    if (Array.isArray(dep)) {
      for (const r of dep) into.required.add(r)
      continue
    }
    if (dep.oneOf?.length) {
      for (const branch of dep.oneOf) {
        if (matchesCondition(branch, values)) {
          mergeBranch(into, branch)
          break
        }
      }
      continue
    }
    mergeBranch(into, dep)
  }
}

/** Resolve visible properties + required set given current answers. */
export function effectiveSchema(
  schema: JsonSchema,
  values: Record<string, unknown>,
): EffectiveSchema {
  const out: EffectiveSchema = {
    properties: { ...(schema.properties ?? {}) },
    required: new Set(schema.required ?? []),
  }

  for (const block of schema.allOf ?? []) {
    if (block.if) {
      if (matchesCondition(block.if, values)) mergeBranch(out, block.then)
      else mergeBranch(out, block.else)
    } else {
      mergeBranch(out, block)
    }
  }

  if (schema.if) {
    if (matchesCondition(schema.if, values)) mergeBranch(out, schema.then)
    else mergeBranch(out, schema.else)
  }

  applyDependencies(out, schema.dependencies, values)
  return out
}

export function defaultAnswers(schema: JsonSchema): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [key, prop] of Object.entries(schema.properties ?? {})) {
    if (prop.default !== undefined) {
      out[key] = prop.default
      continue
    }
    const t = Array.isArray(prop.type) ? prop.type[0] : prop.type
    if (t === 'boolean') out[key] = false
    else if (t === 'array') out[key] = []
    else if (t === 'object') out[key] = defaultAnswers(prop)
  }
  return out
}

export function schemaType(prop: JsonSchema): string {
  if (Array.isArray(prop.type)) return prop.type[0] ?? 'string'
  return prop.type ?? (prop.enum ? 'string' : 'string')
}

export function isMultiSelect(prop: JsonSchema): boolean {
  return schemaType(prop) === 'array' && Boolean(prop.items?.enum)
}

export function pruneAnswers(
  answers: Record<string, unknown>,
  eff: EffectiveSchema,
): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const key of Object.keys(eff.properties)) {
    if (answers[key] === undefined) continue
    const prop = eff.properties[key]
    if (schemaType(prop) === 'object' && isObject(answers[key])) {
      const nested = effectiveSchema(prop, answers[key] as Record<string, unknown>)
      out[key] = pruneAnswers(answers[key] as Record<string, unknown>, nested)
    } else {
      out[key] = answers[key]
    }
  }
  return out
}
